import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Loader2, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

/**
 * Public callback page hit by the Square POS app right after a Tap-to-Pay
 * payment. Reads the iOS `data` JSON or Android `com.squareup.pos.*` query
 * params, posts the result to the backend (`/api/square/pos-confirm`) so
 * SAOR can optimistically mark the booking, then redirects the driver back
 * to their dashboard.
 *
 * The webhook from Square is the canonical source of truth: this page is
 * for immediate user feedback only.
 */
export default function SquarePosCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "loading" });

  const { bookingId, token } = useMemo(() => {
    // 1) Lecture prioritaire depuis localStorage — c'est là qu'on stocke
    //    le booking_id + token avant le redirect vers Square POS, parce
    //    que Square exige une callback URL EXACTE sans query params.
    let fromStorage = null;
    try {
      const raw = localStorage.getItem("saor_square_pending_payment");
      if (raw) {
        const parsed = JSON.parse(raw);
        // 30 minutes max
        if (parsed && Date.now() - (parsed.ts || 0) < 30 * 60 * 1000) {
          fromStorage = parsed;
        }
      }
    } catch (_) {}
    // 2) Fallback sur les query params (utile si on revient depuis un
    //    autre device / sans localStorage).
    return {
      bookingId: fromStorage?.booking_id || params.get("bk") || "",
      token: fromStorage?.token || params.get("tok") || "",
    };
  }, [params]);

  useEffect(() => {
    const parseResult = () => {
      // iOS: ?data=<urlencoded JSON>
      const dataParam = params.get("data");
      if (dataParam) {
        try {
          const decoded = JSON.parse(decodeURIComponent(dataParam));
          if (decoded.error_code) {
            return {
              status: decoded.error_code === "payment_canceled" ? "cancel" : "error",
              transaction_id: decoded.transaction_id,
              client_transaction_id: decoded.client_transaction_id,
              error_code: decoded.error_code,
              error_description: decoded.error_description,
            };
          }
          return {
            status: "success",
            transaction_id: decoded.transaction_id,
            client_transaction_id: decoded.client_transaction_id,
          };
        } catch (e) {
          // fall through to Android branch
        }
      }
      // Android: com.squareup.pos.*
      const errCode = params.get("com.squareup.pos.ERROR_CODE");
      const errDesc = params.get("com.squareup.pos.ERROR_DESCRIPTION");
      const txId =
        params.get("com.squareup.pos.SERVER_TRANSACTION_ID") ||
        params.get("com.squareup.pos.TRANSACTION_ID") ||
        params.get("com.squareup.pos.TENDER_ID");
      const clientTxId = params.get("com.squareup.pos.CLIENT_TRANSACTION_ID");
      if (errCode) {
        return {
          status:
            errCode === "transaction_canceled" || errCode === "payment_canceled"
              ? "cancel"
              : "error",
          error_code: errCode,
          error_description: errDesc,
          transaction_id: txId,
          client_transaction_id: clientTxId,
        };
      }
      if (txId || clientTxId) {
        return {
          status: "success",
          transaction_id: txId,
          client_transaction_id: clientTxId,
        };
      }
      return { status: "unknown" };
    };

    const run = async () => {
      const parsed = parseResult();
      // Notify backend (best-effort).
      if (bookingId && token && parsed.status !== "unknown") {
        try {
          await api.post("/square/pos-confirm", {
            booking_id: bookingId,
            token,
            status: parsed.status,
            transaction_id: parsed.transaction_id || null,
            client_transaction_id: parsed.client_transaction_id || null,
            error_code: parsed.error_code || null,
            error_description: parsed.error_description || null,
          });
        } catch (_) {
          // Ignored — the webhook is the source of truth.
        }
      }
      // Nettoie le pending payment (succès ou échec, on n'y revient pas).
      try {
        localStorage.removeItem("saor_square_pending_payment");
      } catch (_) {}
      setState(parsed);

      // Si Square nous a renvoyés dans Chrome (et non dans la PWA standalone),
      // on force un re-load de l'URL scope-PWA. Sur Android avec une WebAPK
      // installée, Chrome route automatiquement vers la PWA. Sur iOS standalone
      // ce sera no-op. Si on est déjà dans la PWA, navigate() suffit.
      const isStandalone =
        window.matchMedia?.("(display-mode: standalone)").matches ||
        window.navigator.standalone === true;

      const target = parsed.status === "success" && bookingId
        ? `/dashboard/driver/active?bk=${bookingId}&paid=1`
        : "/dashboard/driver";

      // Petit délai pour que l'utilisateur voie le résultat 1 seconde avant
      // le redirect, puis bascule vers la PWA si possible.
      setTimeout(() => {
        if (isStandalone) {
          navigate(target);
        } else {
          // window.location.assign() déclenche le routage Android App Link
          // (WebAPK), ce qui ramène le chauffeur dans la PWA installée.
          window.location.assign(target);
        }
      }, 1200);
    };

    run();
  }, [params, bookingId, token, navigate]);

  const goDashboard = () => navigate("/dashboard/driver");

  if (state.status === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <div className="flex flex-col items-center gap-3 text-muted-foreground">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm">Vérification du paiement Square…</p>
        </div>
      </div>
    );
  }

  let icon, title, color, message;
  if (state.status === "success") {
    icon = <CheckCircle2 className="h-12 w-12 text-emerald-600" />;
    color = "border-emerald-500/40 bg-emerald-50";
    title = "Paiement encaissé";
    message =
      "Square a confirmé le paiement. La course va passer en « Payée » dans quelques secondes.";
  } else if (state.status === "cancel") {
    icon = <XCircle className="h-12 w-12 text-amber-600" />;
    color = "border-amber-400/50 bg-amber-50";
    title = "Paiement annulé";
    message =
      "Vous avez annulé la transaction Square. Vous pouvez réessayer ou choisir un autre mode de paiement.";
  } else if (state.status === "error") {
    icon = <AlertTriangle className="h-12 w-12 text-red-600" />;
    color = "border-red-400/50 bg-red-50";
    title = "Échec du paiement";
    message = state.error_description
      ? `Square : ${state.error_description}`
      : "Le paiement n'a pas pu être traité. Réessayez ou utilisez l'espèces.";
  } else {
    icon = <AlertTriangle className="h-12 w-12 text-muted-foreground" />;
    color = "border-border bg-card";
    title = "Statut inconnu";
    message =
      "Impossible de lire la réponse de Square. Retournez sur le tableau de bord pour vérifier le statut de la course.";
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div
        className={`max-w-md w-full rounded-3xl border-2 ${color} p-8 text-center shadow-sm`}
        data-testid="square-callback-card"
      >
        <div className="flex justify-center">{icon}</div>
        <h1 className="mt-4 font-[Outfit] text-2xl font-semibold">{title}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{message}</p>
        {state.transaction_id && (
          <p className="mt-3 text-[11px] text-muted-foreground">
            Référence Square : {state.transaction_id}
          </p>
        )}
        <p className="mt-4 text-[11px] text-muted-foreground italic">
          Retour à l'application SAOR dans une seconde…
        </p>
        <div className="mt-6 flex flex-col gap-2">
          <Button
            onClick={goDashboard}
            className="rounded-full"
            data-testid="square-callback-back-dashboard"
          >
            Retour au tableau de bord
          </Button>
          {bookingId && (
            <Link
              to={`/dashboard/driver/active?bk=${bookingId}`}
              className="text-xs text-primary underline-offset-2 hover:underline"
              data-testid="square-callback-back-active"
            >
              Revoir la course
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
